// @flow

import React from "react";

import { ServiceManager } from "../service/service-manager";
import { Register } from "../components/register/index";

import { Validation } from "../model/validation";
import { Suburb } from "../model/suburb";
import { LoginLink } from "../components/login-link/index";

import { domainConfig } from "../domain-config";

type Props = {
    serviceManager: ServiceManager
};

type States = {
    email: string,
    firstname: string,
    lastname: string,
    suburb: Suburb,
    postcodeOrSuburb: string,
    submitButtonText: string,
    serverValidateFail: boolean,
    serverValidateMsg: ?string,
    fnameValid: Validation,
    lnameValid: Validation,
    postcodeValid: Validation,
    password1: string,
    password2: string,
    password1Valid: Validation,
    password2Valid: Validation,
    emailValid: Validation,
    isOptIn: string,
    isOptInValid: Validation,
    registering: boolean,
    registerSuccess: boolean
};

export class RegisterContainer extends React.Component<Props, States> {
    state: States;
    constructor(props: Props) {
        super(props);
        this.state = {
            email: "",
            firstname: "",
            lastname: "",
            suburb: {
                codeNumber: "",
                suburb: "",
                state: ""
            },
            postcodeOrSuburb: "",
            password1: "",
            password2: "",
            submitButtonText: "",
            password1Valid: defaultValid("required", "complex"),
            password2Valid: defaultValid("required", "match"),
            emailValid: defaultValid("required", "format"),
            fnameValid: defaultValid("required"),
            lnameValid: defaultValid("required"),
            postcodeValid: defaultValid("required"),
            serverValidateFail: false,
            serverValidateMsg: "Register",
            registering: false,
            registerSuccess: false,
            isOptIn: "",
            isOptInValid: defaultValid("format"),
        };
    }
    register = async (e: Event) => {
        e.preventDefault();
        const allValid = this.validate();
        if (!allValid) {
            return;
        }
        try {
            this.setState({ registering: true });
            await this.props.serviceManager.registerService.register({
                firstName: this.state.firstname,
                lastName: this.state.lastname,
                email: this.state.email,
                password: this.state.password1,
                suburb: this.state.suburb.suburb,
                state: this.state.suburb.state,
                postCode: this.state.suburb.codeNumber,
                isTrade: false,
                isOptIn: this.state.isOptIn,
                accountNumber: null,
            });
            this.setState({ registerSuccess: true });
        } catch (e) {
            this.setState({
                serverValidateFail: true,
                serverValidateMsg: e.errorMessage
            });
        } finally {
            this.setState({ registering: false });
        }
    };

    validate = () => {
        let valid;
        let allValid = true;
        const val = this.props.serviceManager.validationService;

        valid = val.validatePassword(this.state.password1);
        allValid = allValid && valid.success === "success";
        this.setState({ password1Valid: valid });

        valid = val.validatePassword2(
            this.state.password1,
            this.state.password2
        );
        allValid = allValid && valid.success === "success";
        this.setState({ password2Valid: valid });

        valid = val.validateName(this.state.firstname);
        allValid = allValid && valid.success === "success";
        this.setState({ fnameValid: valid });

        valid = val.validateName(this.state.lastname);
        allValid = allValid && valid.success === "success";
        this.setState({ lnameValid: valid });

        valid = val.validateEmail(this.state.email);
        allValid = allValid && valid.success === "success";
        this.setState({ emailValid: valid });

        valid = val.validateSuburb(this.state.suburb);
        allValid = allValid && valid.success === "success";
        this.setState({ postcodeValid: valid });

        return allValid;
    };

    onElementChanged = (e: Event, userInput: string, inputType: string) => {
        const val = this.props.serviceManager.validationService;
        if (inputType === "Password1") {
            const valid = val.validatePassword(userInput);
            this.setState({ password1Valid: valid });
            this.setState({ password1: userInput });
        } else if (inputType === "Password2") {
            const valid = val.validatePassword2(
                this.state.password1,
                userInput
            );
            this.setState({ password2Valid: valid });
            this.setState({ password2: userInput });
        } else if (inputType === "Firstname") {
            const valid = val.validateName(userInput);
            this.setState({ fnameValid: valid });
            this.setState({ firstname: userInput });
        } else if (inputType === "Lastname") {
            const valid = val.validateName(userInput);
            this.setState({ lnameValid: valid });
            this.setState({ lastname: userInput });
        } else if (inputType === "Email") {
            const valid = val.validateEmail(userInput);
            this.setState({ emailValid: valid });
            this.setState({ email: userInput });
        } else if (inputType === "Postcode") {
            const valid = val.validateSuburb(null);
            this.setState({
                postcodeOrSuburb: userInput,
                suburb: null,
                postcodeValid: valid
            });
        } else if (inputType === "IsOptIn") {
            // always returns true, as checkbox is optional
            const valid = {
                success: "success",
                format: false
            };
            this.setState({ isOptInValid: valid });
            this.setState({ isOptIn: userInput })
        }
    };

    doPostcodeQuery = (postcodeOrSuburb: string) => {
        return this.props.serviceManager.postcodeService.queryPostcode(
            postcodeOrSuburb
        );
    };

    onSuburbSelected = (suburb: Suburb) => {
        const valid = this.props.serviceManager.validationService.validateSuburb(
            suburb
        );
        this.setState({
            suburb: suburb,
            postcodeOrSuburb: suburb.codeNumber,
            postcodeValid: valid
        });
    };

    render() {
        return this.state.registerSuccess ? (
            this.getSuccessScreen()
        ) : (
            <Register
                email={this.state.email}
                firstname={this.state.firstname}
                lastname={this.state.lastname}
                fnameValid={this.state.fnameValid}
                lnameValid={this.state.lnameValid}
                password1Valid={this.state.password1Valid}
                password2Valid={this.state.password2Valid}
                postcodeValid={this.state.postcodeValid}
                emailValid={this.state.emailValid}
                postcode={this.state.postcodeOrSuburb}
                password1={this.state.password1}
                password2={this.state.password2}
                isOptIn={this.state.isOptIn}
                isOptInValid={this.state.isOptInValid}
                submitButtonText={this.state.submitButtonText}
                disableSubmit={this.state.registering}
                status={this.state.registering ? "loading" : ""}
                serverValidateFail={this.state.serverValidateFail}
                serverValidateMsg={this.state.serverValidateMsg}
                onElementChanged={this.onElementChanged}
                onSuburbSelected={this.onSuburbSelected}
                onSubmit={this.register}
                onQuerySuburbs={this.doPostcodeQuery}
            />
        );
    }

    getSuccessScreen = () => {
        return (
            <div className="registration-success formSubmissionResponse">
                <h1>Registration Complete</h1>
                <p>
                    Thank you, you have now completed your registration with the{" "}
                    {domainConfig.short_name} website.
                </p>
                <p>
                    <LoginLink className="reece-btn">Login here</LoginLink>
                </p>
            </div>
        );
    };
}

function defaultValid(...keys: string[]) {
    const ret = { success: "" };
    keys.forEach(k => (ret[k] = false));
    return ret;
}
